import React, { useContext } from 'react';

import {
  useStreamRepository,
  usePointRepository,
  useBillingRepository,
  useBusinessProfileRepository,
} from 'application/repositories';

import Container from './DIContainer';
import { KEYS } from './consts';

export type UsePointRepositoryType = typeof usePointRepository;
export type UseStreamRepositoryType = typeof useStreamRepository;
export type UseBillingRepositoryType = typeof useBillingRepository;
export type UseBusinessProfileRepositoryType = typeof useBusinessProfileRepository;

export type DIContainerType = {
  container: typeof Container;
};

export const DIContext = React.createContext<Partial<DIContainerType>>({
  container: {},
} as DIContainerType);

const DIContainerProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  Container.register<UsePointRepositoryType>(KEYS.POINT_REPOSITORY, usePointRepository);
  Container.register<UseStreamRepositoryType>(KEYS.STREAM_REPOSITORY, useStreamRepository);
  Container.register<UseBillingRepositoryType>(KEYS.BILLING_REPOSITORY, useBillingRepository);
  Container.register<UseBusinessProfileRepositoryType>(KEYS.BUSINESS_PROFILE_REPOSITORY, useBusinessProfileRepository);

  return (
    <DIContext.Provider
      value={{
        container: Container,
      }}>
      {children}
    </DIContext.Provider>
  );
};

const useInjection = <T,>(key: symbol): T => {
  const { container } = useContext(DIContext) as DIContainerType;
  const hookByKey = container.get<T>(key);
  return hookByKey;
};

const usePointRepositoryInjector = () => {
  const usePointRepository = useInjection<UsePointRepositoryType>(KEYS.POINT_REPOSITORY);

  const pointRepository = usePointRepository();

  return { ...pointRepository };
};

const useStreamRepositoryInjector = () => {
  const useStreamRepository = useInjection<UseStreamRepositoryType>(KEYS.STREAM_REPOSITORY);

  const streamRepository = useStreamRepository();

  return { ...streamRepository };
};

const useBillingRepositoryInjector = () => {
  const useBillingRepository = useInjection<UseBillingRepositoryType>(KEYS.BILLING_REPOSITORY);

  const billingRepository = useBillingRepository();

  return { ...billingRepository };
};

const useBusinessProfileRepositoryInjector = () => {
  const useBusinessProfileRepository = useInjection<UseBusinessProfileRepositoryType>(KEYS.BUSINESS_PROFILE_REPOSITORY);

  const businessProfileRepository = useBusinessProfileRepository();

  return { ...businessProfileRepository };
};

export {
  useInjection,
  KEYS,
  usePointRepositoryInjector,
  useStreamRepositoryInjector,
  useBillingRepositoryInjector,
  useBusinessProfileRepositoryInjector,
};

export default DIContainerProvider;
